import { formatDistanceToNowStrict, formatDistanceStrict, format, parseISO } from 'date-fns';
import en from 'date-fns/locale/en-US';

export type TokenType = 'xSeconds' | 'xMinutes' | 'xHours' | 'xDays' | 'xMonths' | 'xYears';
const defaultFormatDistance = (token: TokenType, count: number): string => {
  const formatDistanceLocale = {
    xSeconds: `${count}s`,
    xMinutes: `${count}m`,
    xHours: `${count}h`,
    xDays: `${count}d`,
    xMonths: `${count}mo`,
    xYears: `${count}y`,
  };
  return formatDistanceLocale[token];
};

export const distanceToNow = (date: Date | string): string => {
  const dateObj = new Date(date);
  return formatDistanceToNowStrict(dateObj, {
    locale: { formatDistance: defaultFormatDistance },
  });
};

/**
 * This function returns the human readable difference between two dates
 */
export const distanceBetween = (
  dateFrom: Date | string,
  dateTo: Date | string,
  formatDistance = defaultFormatDistance,
): string => {
  const dateFromObj = new Date(dateFrom);
  const dateToObj = new Date(dateTo);
  return formatDistanceStrict(dateFromObj, dateToObj, {
    locale: { formatDistance },
  });
};

export const enMonths = Array.from(Array(12).keys()).map(i => en.localize?.month(i));

/**
 * This function converts date from string format to a user's local time
 *
 * Examples:
 *  - 2021-11-17 -> Nov 17, 2021 (MMM d, yyyy)
 *  - 2021-12-17T22:38:39Z -> Dec 18 (MMM d) [for GMT+0300]
 */
export function getFormattedDate(date: string, outputFormat: string = 'MMM d'): string {
  return format(parseISO(date), outputFormat);
}

/**
 * This function converts date from string format to a user's local time as ISOString
 *
 * Examples:
 *  - null -> null
 *  - 2022-01-11 -> 2022-01-10T21:00:00.000Z [for GMT+0300]
 */
export function getUserISOString(date: null | string): null | string {
  return date && new Date(parseISO(date)).toISOString();
}

/**
 * This function converts date from string|date format to a UTC date to avoid user's timezone affection
 *
 * Examples:
 *  - null -> null
 *  - 2022-01-13T21:00:00.000Z -> 2022-01-14T00:00:00.000Z [for GMT+0300]
 *  - Tue Jan 04 2022 00:00:00 GMT+0300 (Moscow Standard Time) -> 2022-01-04T00:00:00.000Z [for GMT+0300]
 */
export function getDateUTCWithTypeCheck(date: String | Date | null): string | null {
  if (!date) return date;

  let dateAsDate = date;

  // Converting to a date to avoid timezone affection
  if (typeof date === 'string') {
    dateAsDate = new Date(date);
  }

  // @ts-ignore
  return getDateUTC(dateAsDate);
}

export function getDateUTC(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
}

export const emulateUTC = (timeStamp: string | null): string | null => {
  if (!timeStamp) return null;

  const [date] = timeStamp.split('T');
  if (!date) throw new Error(`Something is wrong with the date format, can't extract date from: ${timeStamp}`);

  const parsedDate = parseISO(date);

  return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()).toISOString();
};
